import { useEffect, useState, useCallback, useMemo } from 'react'
import { api, getToken } from '../api'
import { Card, Button, Form, Input, Upload, Empty, Spin, Space, Typography, Row, Col, Drawer, List, message, Popconfirm, Tag, DatePicker, InputNumber, Statistic, Select } from 'antd'
import { PlusOutlined, FileTextOutlined, EnvironmentOutlined, DeleteOutlined, EditOutlined, WhatsAppOutlined, CopyOutlined, AimOutlined, ExperimentOutlined, EuroCircleOutlined, CalendarOutlined, AppstoreOutlined, UserOutlined, FilterOutlined, MailOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import proj4 from 'proj4'
import useIsMobile from '../hooks/useIsMobile'
import { PreguntaTahulla } from './tahullaComun'

const { Title, Text } = Typography

// UTM Zone 30N (ETRS89/WGS84) — usado por SIGPAC en España
proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +units=m +no_defs')

function utmToLatLon(x, y) {
  if (!x || !y) return null
  const [lon, lat] = proj4('EPSG:25830', 'EPSG:4326', [parseFloat(x), parseFloat(y)])
  return { lat: lat.toFixed(6), lon: lon.toFixed(6) }
}

function getMapsUrl(x, y) {
  const coords = utmToLatLon(x, y)
  if (!coords) return null
  return `https://www.google.com/maps?q=${coords.lat},${coords.lon}`
}

function extraerDatosComunes(texto) {
  const get = (regex) => { const m = texto.match(regex); return m ? m[1].trim() : '' }
  return {
    provincia: get(/Provincia:\s*\d+\s*-\s*(.+)/i),
    municipio: get(/Municipio:\s*\d+\s*-\s*(.+)/i),
    poligono: get(/Pol[íi]gono:\s*(\d+)/i),
    num_parcela: get(/Parcela:\s*(\d+)/i),
    referencia_catastral: get(/Referencia Catastral:\s*([A-Z0-9]+)/i),
    coordenadas_x: parseFloat(get(/X:\s*([\d.]+)/i)) || null,
    coordenadas_y: parseFloat(get(/Y:\s*([\d.]+)/i)) || null,
  }
}

function extraerRecintos(texto) {
  const recintos = []
  // El PDF sale sin espacios entre columnas. Usar la sección resumen (C) que es más fiable:
  // Formato: "CA - VIALES0,0117" o "TA - TIERRAS ARABLES0,3742"
  const resumenRegex = /^([A-Z]{2}\s*-\s*[A-Z ]+?)(\d[\d,]*)\s*$/gm
  let match
  let num = 1
  while ((match = resumenRegex.exec(texto)) !== null) {
    const uso = match[1].trim()
    const ha = match[2].replace(',', '.')
    // Ignorar línea "Superficie Total"
    if (uso.includes('Superficie')) continue
    recintos.push({
      numero: String(num++),
      hectareas: ha,
      uso_sigpac: uso,
    })
  }
  // Fallback: intentar con espacios (por si otro formato de PDF sí los tiene)
  if (recintos.length === 0) {
    const fallbackRegex = /^\s*(\d{1,2})\s+([\d,]+)(?:\s+[\d,]+){1,3}\s+([A-Z]{2}\s*-\s*[A-Z\s]+?)(?:\s+\d|$)/gm
    while ((match = fallbackRegex.exec(texto)) !== null) {
      recintos.push({
        numero: match[1],
        hectareas: match[2].replace(',', '.'),
        uso_sigpac: match[3].trim(),
      })
    }
  }
  return recintos
}

function extraerDatosSIGPAC(texto) {
  const comunes = extraerDatosComunes(texto)
  const recintos = extraerRecintos(texto)
  // Para compatibilidad: devolver el primer recinto como datos principales
  const primero = recintos[0] || {}
  return {
    ...comunes,
    hectareas: primero.hectareas || '',
    uso_sigpac: primero.uso_sigpac || '',
  }
}

function haTahullas(hectareas, tahulla_m2) {
  if (!hectareas || !tahulla_m2) return null
  return (parseFloat(hectareas) * 10000 / tahulla_m2)
}

function formatTahullas(hectareas, tahulla_m2) {
  const t = haTahullas(hectareas, tahulla_m2)
  if (t === null) return null
  return t % 1 === 0 ? t.toFixed(0) : t.toFixed(2)
}

export default function Parcelas({ usuario }) {
  const isMobile = useIsMobile()
  const soloLectura = usuario?.rol === 'tecnico' || usuario?.rol === 'usuario'
  const esAdmin = usuario?.rol === 'admin'
  const [parcelas, setParcelas] = useState([])
  const [mostrarForm, setMostrarForm] = useState(false)
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [leyendoPDF, setLeyendoPDF] = useState(false)
  const [recintos, setRecintos] = useState([])
  const [textoSigpac, setTextoSigpac] = useState('')
  const [configMunicipio, setConfigMunicipio] = useState(null)
  const [editando, setEditando] = useState(null)
  const [filtroArrendador, setFiltroArrendador] = useState(null)
  const [filtroFechas, setFiltroFechas] = useState(null) // [dayjs, dayjs]
  const [filtroRenovacion, setFiltroRenovacion] = useState(null)
  const [preguntaTahulla, setPreguntaTahulla] = useState(null) // { municipio, provincia }
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const data = await api('/parcelas')
      setParcelas(data)
    } catch (err) {
      console.error('Error cargando parcelas:', err)
    }
    setCargando(false)
  }

  const buscarConfigMunicipio = useCallback(async (municipio, provincia) => {
    if (!municipio) return
    try {
      const config = await api(`/municipios/buscar?municipio=${encodeURIComponent(municipio)}&provincia=${encodeURIComponent(provincia || '')}`)
      setConfigMunicipio(config)
    } catch (err) {
      console.error('Error buscando config municipio:', err)
    }
  }, [])

  const agregarRecinto = (texto) => {
    const comunes = extraerDatosComunes(texto)
    const recintosExtraidos = extraerRecintos(texto)
    if ((!comunes.poligono && !comunes.referencia_catastral) || recintosExtraidos.length === 0) return

    setRecintos(prev => {
      // Crear un registro por cada recinto encontrado en el PDF
      const nuevosRecintos = recintosExtraidos.map(r => ({
        ...comunes,
        hectareas: r.hectareas,
        uso_sigpac: r.uso_sigpac,
        numero_recinto: r.numero,
      }))
      const nuevo = [...prev, ...nuevosRecintos]
      // Sumar hectáreas de todos los recintos
      const totalHa = nuevo.reduce((sum, r) => sum + (parseFloat(r.hectareas) || 0), 0)
      // Usar datos del primer recinto para provincia/municipio
      const primero = nuevo[0]
      form.setFieldsValue({
        hectareas: totalHa.toFixed(4),
        poligono: primero.poligono,
        num_parcela: primero.num_parcela,
        referencia_catastral: primero.referencia_catastral,
        uso_sigpac: primero.uso_sigpac,
        coordenadas_x: primero.coordenadas_x,
        coordenadas_y: primero.coordenadas_y,
        municipio: primero.municipio,
        provincia: primero.provincia,
      })
      buscarConfigMunicipio(primero.municipio, primero.provincia)
      return nuevo
    })
  }

  const eliminarRecinto = (index) => {
    setRecintos(prev => {
      const nuevo = prev.filter((_, i) => i !== index)
      if (nuevo.length === 0) {
        form.setFieldsValue({ hectareas: '', poligono: '', num_parcela: '', referencia_catastral: '', uso_sigpac: '', coordenadas_x: null, coordenadas_y: null, municipio: '', provincia: '' })
        setConfigMunicipio(null)
      } else {
        const totalHa = nuevo.reduce((sum, r) => sum + (parseFloat(r.hectareas) || 0), 0)
        const primero = nuevo[0]
        form.setFieldsValue({
          hectareas: totalHa.toFixed(4),
          poligono: primero.poligono,
          num_parcela: primero.num_parcela,
          referencia_catastral: primero.referencia_catastral,
          municipio: primero.municipio,
          provincia: primero.provincia,
        })
      }
      return nuevo
    })
  }

  const leerPDF = async (file) => {
    setLeyendoPDF(true)
    try {
      const formData = new FormData()
      formData.append('pdf', file)
      const token = getToken()
      const res = await fetch('/api/parcelas/sigpac-pdf', {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData,
      })
      const data = await res.json()
      if (data.texto) {
        agregarRecinto(data.texto)
      } else {
        message.error('No se pudo extraer texto del PDF')
      }
    } catch (err) {
      console.error('Error leyendo PDF:', err)
      message.error('Error procesando el PDF')
    }
    setLeyendoPDF(false)
    return false
  }

  const cerrarForm = () => {
    setMostrarForm(false)
    setEditando(null)
    setRecintos([])
    setTextoSigpac('')
    setConfigMunicipio(null)
    form.resetFields()
  }

  const abrirEdicion = (parcela) => {
    setEditando(parcela)
    setRecintos(parcela.recintos || [])
    form.setFieldsValue({
      nombre: parcela.nombre,
      hectareas: parcela.hectareas,
      poligono: parcela.poligono,
      num_parcela: parcela.num_parcela,
      referencia_catastral: parcela.referencia_catastral,
      uso_sigpac: parcela.uso_sigpac,
      coordenadas_x: parcela.coordenadas_x,
      coordenadas_y: parcela.coordenadas_y,
      municipio: parcela.municipio,
      provincia: parcela.provincia,
      fecha_arriendo: parcela.fecha_arriendo ? dayjs(parcela.fecha_arriendo) : null,
      precio_arriendo_tahulla: parcela.precio_arriendo_tahulla,
      arrendador: parcela.arrendador || '',
      renovacion_anios: parcela.renovacion_anios || null,
    })
    if (parcela.municipio) buscarConfigMunicipio(parcela.municipio, parcela.provincia)
    setMostrarForm(true)
  }

  const enviarMailPrueba = async (id) => {
    try {
      const r = await api(`/parcelas/${id}/test-renovacion-mail`, { method: 'POST' })
      message.success(`Mail enviado (${r.dias} días para la renovación)`)
    } catch (err) {
      message.error(err.message || 'No se pudo enviar el mail de prueba')
    }
  }

  const eliminarParcela = async (id) => {
    try {
      await api(`/parcelas/${id}`, { method: 'DELETE' })
      message.success('Parcela eliminada')
      cargar()
    } catch (err) {
      console.error('Error eliminando parcela:', err)
      message.error('Error eliminando parcela')
    }
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      const datos = {
        nombre: values.nombre,
        hectareas: values.hectareas,
        poligono: values.poligono || '',
        num_parcela: values.num_parcela || '',
        referencia_catastral: values.referencia_catastral || '',
        uso_sigpac: values.uso_sigpac || '',
        coordenadas_x: values.coordenadas_x || null,
        coordenadas_y: values.coordenadas_y || null,
        municipio: values.municipio || '',
        provincia: values.provincia || '',
        recintos: recintos,
      }
      if (esAdmin) {
        datos.fecha_arriendo = values.fecha_arriendo ? values.fecha_arriendo.format('YYYY-MM-DD') : null
        datos.precio_arriendo_tahulla = values.precio_arriendo_tahulla ?? null
        datos.arrendador = values.arrendador?.trim() || null
        datos.renovacion_anios = values.renovacion_anios || null
      }
      if (editando) {
        await api(`/parcelas/${editando.id}`, { method: 'PUT', body: JSON.stringify(datos) })
        message.success('Parcela actualizada')
      } else {
        await api('/parcelas', { method: 'POST', body: JSON.stringify(datos) })
        message.success('Parcela creada')
      }
      cerrarForm()
      cargar()
      comprobarTahulla(datos.municipio, datos.provincia)
    } catch (err) {
      console.error('Error guardando parcela:', err)
      message.error('Error guardando parcela')
    }
    setGuardando(false)
  }

  // Si el pueblo de la parcela no tiene tahúlla configurada, no se pueden calcular sus
  // tahúllas: el administrador lo rellena en el momento; los demás reciben un aviso.
  const comprobarTahulla = async (municipio, provincia) => {
    if (!municipio || !provincia) return
    try {
      const config = await api(`/municipios/buscar?municipio=${encodeURIComponent(municipio)}&provincia=${encodeURIComponent(provincia)}`)
      if (config) return
      if (esAdmin) setPreguntaTahulla({ municipio, provincia })
      else message.warning(`${municipio} no tiene la tahúlla configurada: avisa al administrador para que se calculen las tahúllas`)
    } catch (_) {}
  }

  const totalHaRecintos = recintos.reduce((sum, r) => sum + (parseFloat(r.hectareas) || 0), 0)

  const parcelasFiltradas = useMemo(() => {
    return parcelas.filter(p => {
      if (filtroArrendador && p.arrendador !== filtroArrendador) return false
      if (filtroRenovacion != null && p.renovacion_anios !== filtroRenovacion) return false
      if (filtroFechas && filtroFechas[0] && filtroFechas[1]) {
        if (!p.fecha_arriendo) return false
        const f = dayjs(p.fecha_arriendo)
        if (f.isBefore(filtroFechas[0], 'day') || f.isAfter(filtroFechas[1], 'day')) return false
      }
      return true
    })
  }, [parcelas, filtroArrendador, filtroFechas, filtroRenovacion])

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <PreguntaTahulla
        pueblo={preguntaTahulla}
        onGuardado={() => { setPreguntaTahulla(null); cargar() }}
        onCancelar={() => setPreguntaTahulla(null)}
      />
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Parcelas</Title>
        {!soloLectura && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setMostrarForm(true)}
            style={{ background: '#2E7D32', flexShrink: 0 }}
          >
            {!isMobile && 'Nueva'}
          </Button>
        )}
      </div>

      <Drawer
        title={editando ? 'Editar parcela' : 'Nueva parcela'}
        open={mostrarForm}
        onClose={cerrarForm}
        width={isMobile ? '100%' : 420}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={cerrarForm}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>
              {editando ? 'Actualizar' : 'Guardar parcela'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar} initialValues={{}}>
          {/* Importar SIGPAC */}
          <Card size="small" style={{ marginBottom: '16px', background: '#f6ffed', borderColor: '#b7eb8f' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong style={{ fontSize: '13px' }}>
                <FileTextOutlined /> Importar desde SIGPAC
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Puedes añadir varios informes para combinar recintos
              </Text>
              <Upload.Dragger
                accept=".pdf"
                beforeUpload={leerPDF}
                showUploadList={false}
                style={{ padding: '8px' }}
              >
                {leyendoPDF ? <Spin tip="Leyendo PDF..." /> : (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Arrastra o pulsa para subir PDF
                  </Text>
                )}
              </Upload.Dragger>
              <Input.TextArea
                placeholder="O pega aquí el texto copiado del PDF del SIGPAC..."
                rows={3}
                value={textoSigpac}
                onChange={(e) => setTextoSigpac(e.target.value)}
                style={{ fontSize: '12px', marginTop: '4px' }}
              />
              <Button
                size="small"
                type="dashed"
                disabled={textoSigpac.length < 50}
                onClick={() => { agregarRecinto(textoSigpac); setTextoSigpac('') }}
                style={{ width: '100%' }}
              >
                <PlusOutlined /> Añadir recinto
              </Button>
            </Space>
          </Card>

          {/* Lista de recintos añadidos */}
          {recintos.length > 0 && (
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                Recintos añadidos ({recintos.length})
              </Text>
              <List
                size="small"
                dataSource={recintos}
                renderItem={(r, i) => (
                  <List.Item
                    style={{ padding: '4px 0' }}
                    actions={[
                      <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => eliminarRecinto(i)} />
                    ]}
                  >
                    <Text style={{ fontSize: '12px' }}>
                      {r.numero_recinto && <Text type="secondary" style={{ fontSize: '11px' }}>R{r.numero_recinto} </Text>}
                      {r.uso_sigpac && <Text style={{ fontSize: '11px', color: r.uso_sigpac.startsWith('CA') ? '#cf1322' : '#389e0d' }}>{r.uso_sigpac} </Text>}
                      — {r.hectareas} ha
                      {configMunicipio && <Text type="success" style={{ fontSize: '12px' }}> ({formatTahullas(r.hectareas, configMunicipio.tahulla_m2)} th)</Text>}
                    </Text>
                  </List.Item>
                )}
              />
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '8px', marginTop: '4px' }}>
                <Text strong style={{ fontSize: '13px' }}>
                  Total: {totalHaRecintos.toFixed(4)} ha
                  {configMunicipio && <Text type="success" strong style={{ fontSize: '13px' }}> = {formatTahullas(totalHaRecintos, configMunicipio.tahulla_m2)} tahúllas</Text>}
                </Text>
              </div>
            </Card>
          )}

          {/* Datos básicos */}
          <Form.Item label="Nombre de la parcela" name="nombre" rules={[{ required: true, message: 'El nombre es obligatorio' }]}>
            <Input placeholder="Ej: Parcela Norte, La Rambla..." />
          </Form.Item>

          <Form.Item label="Hectáreas" name="hectareas">
            <Input type="number" step="0.0001" placeholder="Ej: 0.2989" />
          </Form.Item>

          {/* Datos SIGPAC */}
          <Card size="small" title={<Text style={{ fontSize: '13px' }}><EnvironmentOutlined /> Datos SIGPAC / Catastro</Text>} style={{ marginTop: '16px' }}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Provincia" name="provincia">
                  <Input placeholder="Ej: ALICANTE" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Municipio" name="municipio">
                  <Input placeholder="Ej: COX" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Polígono" name="poligono">
                  <Input placeholder="Ej: 8" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Parcela" name="num_parcela">
                  <Input placeholder="Ej: 41" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Ref. Catastral" name="referencia_catastral">
              <Input placeholder="Ej: 03058A008000410000RD" />
            </Form.Item>
            <Form.Item label="Uso SIGPAC" name="uso_sigpac">
              <Input placeholder="Ej: TA - TIERRAS ARABLES" />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Coord. X (UTM)" name="coordenadas_x">
                  <Input placeholder="Ej: 686252.48" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Coord. Y (UTM)" name="coordenadas_y">
                  <Input placeholder="Ej: 4223713.63" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {esAdmin && (
            <Card
              size="small"
              title={<Text style={{ fontSize: '13px' }}><EuroCircleOutlined /> Arriendo</Text>}
              style={{ marginTop: '16px', background: '#fffbe6', borderColor: '#ffe58f' }}
            >
              <Form.Item label="Arrendador" name="arrendador">
                <Input placeholder="Nombre del propietario" prefix={<UserOutlined />} />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Fecha de pago" name="fecha_arriendo">
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Precio por tahúlla (€)" name="precio_arriendo_tahulla">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="Ej: 50" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Renovación cada" name="renovacion_anios" extra="Recibirás un mail un mes antes de cada renovación.">
                <Select allowClear placeholder="Sin renovación automática" options={[1,2,3,4,5,6,7,8,9,10].map(n => ({ value: n, label: `${n} año${n === 1 ? '' : 's'}` }))} />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                El total se calcula automáticamente según las tahúllas. Visible solo para administradores.
              </Text>
            </Card>
          )}
        </Form>
      </Drawer>

      {/* Listado de parcelas */}
      <div style={{ padding: '16px' }}>
        {!cargando && esAdmin && parcelas.some(p => p.arrendador || p.fecha_arriendo || p.renovacion_anios) && (
          <Card size="small" style={{ marginBottom: '12px', borderRadius: '10px' }}>
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24} sm={2} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FilterOutlined style={{ color: '#1B3A6B' }} />
                <Text strong style={{ fontSize: '12px' }}>Filtros</Text>
              </Col>
              <Col xs={24} sm={7}>
                <Select
                  allowClear
                  placeholder="Todos los arrendadores"
                  value={filtroArrendador}
                  onChange={setFiltroArrendador}
                  style={{ width: '100%' }}
                  suffixIcon={<UserOutlined />}
                  options={[...new Set(parcelas.map(p => p.arrendador).filter(Boolean))].sort().map(a => ({ value: a, label: a }))}
                />
              </Col>
              <Col xs={24} sm={5}>
                <Select
                  allowClear
                  placeholder="Renovación"
                  value={filtroRenovacion}
                  onChange={setFiltroRenovacion}
                  style={{ width: '100%' }}
                  options={[...new Set(parcelas.map(p => p.renovacion_anios).filter(Boolean))].sort((a,b)=>a-b).map(n => ({ value: n, label: `${n} año${n === 1 ? '' : 's'}` }))}
                />
              </Col>
              <Col xs={24} sm={8}>
                <DatePicker.RangePicker
                  value={filtroFechas}
                  onChange={setFiltroFechas}
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder={['Pago desde', 'Pago hasta']}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={2}>
                <Button
                  size="small"
                  type="link"
                  disabled={!filtroArrendador && !filtroFechas && filtroRenovacion == null}
                  onClick={() => { setFiltroArrendador(null); setFiltroFechas(null); setFiltroRenovacion(null) }}
                >
                  Limpiar
                </Button>
              </Col>
            </Row>
          </Card>
        )}
        {!cargando && parcelas.length > 0 && (() => {
          const filtradas = parcelasFiltradas
          const total = filtradas.length
          const sumarTahullas = (lista) => lista.reduce((s, p) => s + (haTahullas(p.hectareas, p.tahulla_m2) || 0), 0)
          const sumarHa = (lista) => lista.reduce((s, p) => s + (parseFloat(p.hectareas) || 0), 0)
          const formatTh = (v) => v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)
          const enCultivo = filtradas.filter(p => p.en_cultivo)
          const barbecho = filtradas.filter(p => !p.en_cultivo)
          const sinTahulla = filtradas.filter(p => !p.tahulla_m2).length
          return (
            <Row gutter={[8, 8]} style={{ marginBottom: '12px' }}>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ borderRadius: '10px' }}>
                  <Statistic title="Parcelas" value={total} prefix={<AppstoreOutlined style={{ color: '#1B3A6B' }} />} valueStyle={{ fontSize: '20px', color: '#1B3A6B' }} />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {formatTh(sumarTahullas(parcelas))} tahúllas <Text type="secondary" style={{ fontSize: '10px' }}>({sumarHa(parcelas).toFixed(2)} ha)</Text>
                  </Text>
                  {sinTahulla > 0 && (() => {
                    const sinConfig = filtradas.find(p => !p.tahulla_m2 && p.municipio && p.provincia)
                    return (
                      <div>
                        <Text type="warning" style={{ fontSize: '10px' }}>{sinTahulla} sin tahúlla configurada</Text>
                        {esAdmin && sinConfig && (
                          <Button type="link" size="small" style={{ fontSize: '10px', padding: 0, height: 'auto', marginLeft: '4px' }}
                            onClick={() => setPreguntaTahulla({ municipio: sinConfig.municipio, provincia: sinConfig.provincia })}>
                            Configurar
                          </Button>
                        )}
                      </div>
                    )
                  })()}
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ borderRadius: '10px', background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Statistic title="En cultivo" value={enCultivo.length} prefix={<ExperimentOutlined style={{ color: '#2E7D32' }} />} valueStyle={{ fontSize: '20px', color: '#2E7D32' }} />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {formatTh(sumarTahullas(enCultivo))} tahúllas <Text type="secondary" style={{ fontSize: '10px' }}>({sumarHa(enCultivo).toFixed(2)} ha)</Text>
                  </Text>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ borderRadius: '10px', background: '#fffbe6', borderColor: '#ffe58f' }}>
                  <Statistic title="Barbecho" value={barbecho.length} prefix={<EnvironmentOutlined style={{ color: '#d48806' }} />} valueStyle={{ fontSize: '20px', color: '#d48806' }} />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {formatTh(sumarTahullas(barbecho))} tahúllas <Text type="secondary" style={{ fontSize: '10px' }}>({sumarHa(barbecho).toFixed(2)} ha)</Text>
                  </Text>
                </Card>
              </Col>
              {esAdmin && (() => {
                const totalArriendo = filtradas.reduce((s, p) => {
                  const th = haTahullas(p.hectareas, p.tahulla_m2)
                  const precio = parseFloat(p.precio_arriendo_tahulla)
                  return th != null && !Number.isNaN(precio) ? s + th * precio : s
                }, 0)
                const arrendadas = filtradas.filter(p => p.precio_arriendo_tahulla != null)
                const sinTahullaArriendo = arrendadas.filter(p => !p.tahulla_m2).length
                return (
                  <Col xs={12} sm={6}>
                    <Card size="small" style={{ borderRadius: '10px', background: '#fff1f0', borderColor: '#ffa39e' }}>
                      <Statistic
                        title="Arriendo total"
                        value={totalArriendo}
                        precision={2}
                        suffix="€"
                        prefix={<EuroCircleOutlined style={{ color: '#cf1322' }} />}
                        valueStyle={{ fontSize: '20px', color: '#cf1322' }}
                      />
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {arrendadas.length} arrendadas
                      </Text>
                      {sinTahullaArriendo > 0 && (
                        <div><Text type="warning" style={{ fontSize: '10px' }}>{sinTahullaArriendo} sin tahúllas</Text></div>
                      )}
                    </Card>
                  </Col>
                )
              })()}
            </Row>
          )
        })()}
        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}>
            <Spin size="large" />
          </div>
        ) : parcelas.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4}>
                <Text type="secondary">No hay parcelas todavía</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>Pulsa "Nueva" para añadir la primera</Text>
              </Space>
            }
            style={{ marginTop: '60px' }}
          />
        ) : parcelasFiltradas.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">Ninguna parcela coincide con los filtros</Text>}
            style={{ marginTop: '40px' }}
          />
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {parcelasFiltradas.map((p, i) => {
              const tahullas = formatTahullas(p.hectareas, p.tahulla_m2)
              const recintosData = p.recintos || []
              const coords = utmToLatLon(p.coordenadas_x, p.coordenadas_y)
              const mapsUrl = getMapsUrl(p.coordenadas_x, p.coordenadas_y)
              return (
                <Card key={p.id || i} size="small" hoverable style={{ borderRadius: '12px', borderLeft: `3px solid ${p.en_cultivo ? '#52c41a' : '#d48806'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <Space size={6} wrap>
                        <Text strong style={{ fontSize: '15px', color: '#1B3A6B' }}>{p.nombre}</Text>
                        <Tag color={p.en_cultivo ? 'success' : 'warning'} icon={p.en_cultivo ? <ExperimentOutlined /> : <EnvironmentOutlined />} style={{ margin: 0 }}>
                          {p.en_cultivo ? 'En cultivo' : 'Barbecho'}
                        </Tag>
                      </Space>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {tahullas
                          ? <><Text strong style={{ fontSize: '12px', color: '#2E7D32' }}>{tahullas} tahúllas</Text> <Text type="secondary" style={{ fontSize: '11px' }}>({p.hectareas} ha)</Text></>
                          : <>{p.hectareas} ha</>
                        }
                      </Text>
                    </div>
                    {!soloLectura && (
                      <Space size={4}>
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirEdicion(p)} />
                        <Popconfirm title="¿Eliminar esta parcela?" onConfirm={() => eliminarParcela(p.id)} okText="Sí" cancelText="No">
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )}
                  </div>

                  {p.referencia_catastral && (
                    <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '8px 10px', marginTop: '10px' }}>
                      <Text style={{ fontSize: '11px', color: '#888' }}>
                        <EnvironmentOutlined /> Pol. {p.poligono} · Parc. {p.num_parcela} · {p.municipio}, {p.provincia}
                      </Text>
                      <br />
                      <Text style={{ fontSize: '11px', color: '#aaa' }}>Ref: {p.referencia_catastral}</Text>
                      {recintosData.length > 1 && (
                        <Text style={{ fontSize: '11px', color: '#aaa', display: 'block' }}>
                          {recintosData.length} recintos combinados
                        </Text>
                      )}
                    </div>
                  )}

                  {esAdmin && (p.precio_arriendo_tahulla != null || p.fecha_arriendo || p.arrendador || p.renovacion_anios) && (() => {
                    const tahullasNum = haTahullas(p.hectareas, p.tahulla_m2)
                    const precioTh = parseFloat(p.precio_arriendo_tahulla)
                    const total = (tahullasNum != null && !Number.isNaN(precioTh)) ? tahullasNum * precioTh : null
                    let proximaRenov = null
                    let diasRenov = null
                    if (p.fecha_arriendo && p.renovacion_anios > 0) {
                      let prox = dayjs(p.fecha_arriendo)
                      const hoy = dayjs().startOf('day')
                      while (prox.isBefore(hoy, 'day')) prox = prox.add(p.renovacion_anios, 'year')
                      proximaRenov = prox
                      diasRenov = prox.diff(hoy, 'day')
                    }
                    return (
                      <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '8px', padding: '8px 10px', marginTop: '8px' }}>
                        {p.arrendador && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <UserOutlined style={{ color: '#8c6213' }} />
                            <Text strong style={{ fontSize: '12px', color: '#8c6213' }}>{p.arrendador}</Text>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <EuroCircleOutlined style={{ color: '#d48806' }} />
                          <Text style={{ fontSize: '12px', flex: 1 }}>
                            {!Number.isNaN(precioTh) && (
                              <Text strong style={{ fontSize: '12px', color: '#d48806' }}>
                                {precioTh.toFixed(2)} €/tahúlla
                              </Text>
                            )}
                            {p.fecha_arriendo && (
                              <Text type="secondary" style={{ fontSize: '11px', marginLeft: '6px' }}>
                                <CalendarOutlined /> Pago: {dayjs(p.fecha_arriendo).format('DD/MM/YYYY')}
                              </Text>
                            )}
                          </Text>
                        </div>
                        {total != null && (
                          <Text style={{ fontSize: '11px', color: '#8c6213', display: 'block', marginTop: '2px' }}>
                            Total: <Text strong style={{ fontSize: '12px', color: '#8c6213' }}>{total.toFixed(2)} €</Text>
                            {' '}<Text type="secondary" style={{ fontSize: '10px' }}>({(tahullasNum % 1 === 0 ? tahullasNum.toFixed(0) : tahullasNum.toFixed(2))} tahúllas × {precioTh.toFixed(2)} €)</Text>
                          </Text>
                        )}
                        {total == null && !Number.isNaN(precioTh) && (
                          <Text type="warning" style={{ fontSize: '10px', display: 'block', marginTop: '2px' }}>
                            No se puede calcular el total: falta configurar tahúllas para este municipio.
                          </Text>
                        )}
                        {p.renovacion_anios > 0 && (
                          <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                            <Text style={{ fontSize: '11px', color: '#8c6213' }}>
                              <CalendarOutlined /> Renovación cada {p.renovacion_anios} año{p.renovacion_anios === 1 ? '' : 's'}
                              {proximaRenov && (
                                <>
                                  {' · próxima '}
                                  <Text strong style={{ fontSize: '11px', color: '#8c6213' }}>{proximaRenov.format('DD/MM/YYYY')}</Text>
                                  {diasRenov != null && diasRenov <= 25 && diasRenov > 0 && (
                                    <Tag color="red" style={{ marginLeft: '6px', fontSize: '10px' }}>en {diasRenov} día{diasRenov === 1 ? '' : 's'}</Tag>
                                  )}
                                </>
                              )}
                            </Text>
                            <Button size="small" type="link" icon={<MailOutlined />} onClick={() => enviarMailPrueba(p.id)} style={{ padding: 0, fontSize: '11px', height: 'auto' }}>
                              Enviar mail de prueba
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* Mapa y botones de ubicación */}
                  {coords && (
                    <div style={{ marginTop: '10px' }}>
                      <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', height: '100px', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                        <iframe
                          title={`Mapa ${p.nombre}`}
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${coords.lon - 0.003},${coords.lat - 0.002},${parseFloat(coords.lon) + 0.003},${parseFloat(coords.lat) + 0.002}&layer=mapnik&marker=${coords.lat},${coords.lon}`}
                          style={{ width: '100%', height: '130px', border: 'none', pointerEvents: 'none', marginBottom: '-30px' }}
                        />
                      </a>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                        <Button
                          size="small"
                          type="primary"
                          icon={<AimOutlined />}
                          href={mapsUrl}
                          target="_blank"
                          style={{ flex: 1, fontSize: '12px', background: '#1B3A6B' }}
                        >
                          Ver mapa
                        </Button>
                        <Button
                          size="small"
                          icon={<WhatsAppOutlined />}
                          style={{ flex: 1, fontSize: '12px', background: '#25D366', color: 'white', borderColor: '#25D366' }}
                          onClick={() => {
                            const texto = `Parcela: ${p.nombre}\nUbicación: ${mapsUrl}`
                            window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank')
                          }}
                        >
                          Enviar
                        </Button>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          style={{ fontSize: '12px' }}
                          onClick={() => {
                            navigator.clipboard.writeText(mapsUrl)
                            message.success('Enlace copiado')
                          }}
                        />
                      </div>
                    </div>
                  )}
                </Card>
              )
            })}
          </Space>
        )}
      </div>
    </div>
  )
}
